import {
  Modal,
  Stack,
  TextField,
  Text,
  IStackTokens,
  PrimaryButton,
  DefaultButton,
  ComboBox,
  Spinner, SpinnerSize, TooltipHost, IconButton, MessageBarType, MessageBar,
} from '@fluentui/react';
import { Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toDotNetTimeSpan } from '../../../../shared/utils/date';
import { notify } from '../../../common/utils/notification';
import { useGeneratePublicApiToken } from '../api/useGeneratePublicApiToken';
import { PublicApiFormState } from '../types/publicApi';

import publicApiStyles from '../publicApi.module.css';

const verticalGapStackTokens: IStackTokens = {
  childrenGap: 10,
  padding: 12,
};

const initialValues: PublicApiFormState = {
  name: '',
  timeToLive: '',
};

const modalWidth = { main: { width: 400 } };

export const CreateTokenBtn = () => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [tokenToShow, setTokenToShow] = useState('');
  const [copied, setCopied] = useState(false);
  const { mutate, isLoading: isTokenCreating } = useGeneratePublicApiToken();
  const { t } = useTranslation();

  const isTokenToShowModalOpened = !!tokenToShow;

  const handleCloseDisplayTokenModal = () => {
    setTokenToShow('');
  }

  const handleCloseModal = () => {
    if (isTokenCreating) {
      return;
    }

    setIsModalOpened(false);
  };

  const handleCreateToken = (values: PublicApiFormState, formikHelpers: FormikHelpers<PublicApiFormState>) => {
    const { name, timeToLive } = values;

    mutate({
      name,
      timeToLive: toDotNetTimeSpan(timeToLive),
    }, {
      onSuccess: async ({ data }) => {
        setTokenToShow(data.clientSecret);
        handleCloseModal();
        formikHelpers.resetForm();
        formikHelpers.setSubmitting(false);
      },
      onError: () => {
        notify.error(t('common:tokenGenerationError'));
        formikHelpers.setSubmitting(false);
        handleCloseModal();
      }
    });
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(tokenToShow);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div
        style={{ display: 'flex', justifyContent: 'flex-end' }}
      >
        <PrimaryButton
          disabled={false}
          text={t('common:generateClientSecret')}
          allowDisabledFocus
          onClick={() => setIsModalOpened(true)}
          style={{ marginBottom: 12 }}
        />
      </div>

      <Modal
        isOpen={isModalOpened}
        isBlocking
        onDismiss={handleCloseModal}
        styles={modalWidth}
      >
        <Formik
          initialValues={initialValues}
          validate={(values) => {
            const errors: Partial<Record<keyof PublicApiFormState, string>> = {};

            if (!values.name.trim()) {
              errors.name = t('common:tokenGenerationError');
            }

            if (!values.timeToLive) {
              errors.timeToLive = t('common:ttlIsRequired');
            }

            return errors;
          }}
          onSubmit={handleCreateToken}
        >
          {({
              values,
              errors,
              touched,
              isSubmitting,
              handleSubmit,
              setFieldValue,
            }) => (
            <form onSubmit={handleSubmit}>
              <Stack tokens={verticalGapStackTokens}>
                <Text variant="xLarge">{t('common:generateNewSecretModalTitle')}</Text>

                <TextField
                  label={t('common:clientSecretName')}
                  required
                  value={values.name}
                  errorMessage={touched.name ? errors.name : undefined}
                  onChange={(_, v) => setFieldValue('name', v || '')}
                />

                <ComboBox
                  label={t('common:ttl')}
                  required
                  selectedKey={values.timeToLive}
                  errorMessage={touched.timeToLive ? errors.timeToLive : undefined}
                  options={[
                    { key: '365', text: t('common:year') },
                    { key: '182', text: t('common:6_Months') },
                    { key: '91', text: t('common:3_Months') },
                    { key: '30', text: t('common:1_Month') },
                    { key: '1', text: t('common:day') },
                  ]}
                  onChange={(_, option) =>
                    setFieldValue('timeToLive', option?.key ?? '')
                  }
                />

                <div className={publicApiStyles.modalButtonsWrapper}>
                  <DefaultButton
                    text={t('common:cancel')}
                    disabled={isSubmitting}
                    onClick={handleCloseModal}
                  />

                  <PrimaryButton
                    type="submit"
                    text={t('common:create')}
                    disabled={isSubmitting}
                    onRenderIcon={() =>
                      isSubmitting ? <Spinner size={SpinnerSize.small} /> : null
                    }
                  />
                </div>
              </Stack>
            </form>
          )}
        </Formik>
      </Modal>

      <Modal
        styles={modalWidth}
        isOpen={isTokenToShowModalOpened}
        isBlocking
        onDismiss={handleCloseDisplayTokenModal}
        isClickableOutsideFocusTrap={false}
      >
        <Stack tokens={verticalGapStackTokens}>
          <Text
            variant="xLarge"
            block
            style={{ marginBottom: 4 }}
          >{t('common:newGeneratedSecret')}</Text>

          <MessageBar
            delayedRender={false}
            messageBarType={MessageBarType.warning}
          >
            {t('common:newGeneratedTokenCaption')}
          </MessageBar>

          <Stack
            horizontal
            verticalAlign="end"
            tokens={{ childrenGap: 8 }}
          >
            <TextField
              label={t('common:clientSecretKey')}
              value={tokenToShow}
              readOnly
              style={{
              }}
              styles={{
                root: { width: '100%' }
              }}
            />
            <TooltipHost
              content={copied ? `${t('common:copied')}!` : t('common:copy')}
            >
              <IconButton
                iconProps={{ iconName: 'Copy' }}
                ariaLabel="Copy token"
                onClick={handleCopy}
              />
            </TooltipHost>
          </Stack>

          <div className={publicApiStyles.modalButtonsWrapper}>
            <PrimaryButton
              disabled={isTokenCreating}
              text={t('common:close')}
              allowDisabledFocus
              onClick={handleCloseDisplayTokenModal}
            />
          </div>
        </Stack>
      </Modal>
    </>
  );
};